// @cortex/i18n/server — server-safe entrypoint.
//
// Re-exports the dictionaries, types, and a locale-aware translation
// function (`tForLang`) without pulling in the React hooks that the
// default entrypoint requires. Use this from backend code (Hono routes,
// emailSender, cron jobs) that needs to render strings per-request
// without a browser-scoped LangStore.
//
// Why this exists: index.ts imports react and exposes useLang/LangStore
// that depend on `window`. Server packages (e.g. @cortex/email-templates,
// @cortex/api) shouldn't need React just to translate a key. This module
// gives them a React-free door into the same dictionaries.

import de from './locales/de';
import en from './locales/en';
import es from './locales/es';
import fr from './locales/fr';
import it from './locales/it';
import nl from './locales/nl';
import pl from './locales/pl';
import type { Lang, TranslationKey, TranslationVars } from './types';

export type { Lang, TranslationKey, TranslationVars } from './types';
export { DEFAULT_LANG, SUPPORTED_LANGS, isSupportedLang } from './types';

export const DICTIONARIES: Record<Lang, Record<string, string>> = {
  de: de as unknown as Record<string, string>,
  en,
  fr,
  es,
  it,
  nl,
  pl,
};

// ---------------------------------------------------------------------------
// tForLang() — locale-aware lookup with optional variable interpolation.
//
// Fallback chain: target locale → DE → '[key]' (bracket-wrapped for
// visual spotting during QA). Mirrors t()'s fallback behavior so client
// and server resolve the same string for the same key.
//
// Variable interpolation: '{name}' single-brace syntax. Pass
// `{ escapeHtml: true }` when injecting variables into HTML contexts
// so untrusted values can't break markup or inject script tags.
// Defaults to false to stay behavior-compatible with t() for non-HTML
// callers.
// ---------------------------------------------------------------------------

export function tForLang(
  key: TranslationKey | string,
  lang: Lang,
  vars?: TranslationVars,
  opts?: { escapeHtml?: boolean },
): string {
  const dict = DICTIONARIES[lang];
  let s = dict?.[key] ?? (de as Record<string, string>)[key] ?? `[${key}]`;
  if (vars) {
    const shouldEscape = opts?.escapeHtml === true;
    for (const [k, v] of Object.entries(vars)) {
      const replacement = shouldEscape ? escapeHtmlValue(String(v)) : String(v);
      // LEA-418: ALLE Vorkommen ersetzen — identischer Bug wie in t() (index.ts).
      // split/join statt replaceAll: kein $-Sonderzeichen-Handling im Replacement,
      // wichtig weil hier user-generierte Werte in Mails landen.
      s = s.split(`{${k}}`).join(replacement);
    }
  }
  return s;
}

function escapeHtmlValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
